import { useQueryClient } from "@tanstack/react-query";
import { Link, Outlet, useNavigate } from "@tanstack/react-router";
import { LoaderCircle, LogOut, Menu } from "lucide-react";
import { useEffect } from "react";
import { useObraActiva } from "@/app/obra-activa";
import { Button } from "@/components/ui/button";
import { useLogout, useMe } from "@/lib/auth";
import { cn } from "@/lib/cn";
import { startConnectivityMonitor } from "@/lib/online-status";
import { flushOutbox } from "@/lib/sync-runner";
import { ObraSwitcher } from "./ObraSwitcher";
import { navItemsPara, Sidebar } from "./Sidebar";
import { SyncIndicator } from "./SyncIndicator";
import { useSidebar } from "./useSidebar";

/**
 * AppLayout es el chasis de la aplicación autenticada: en escritorio reúne marca, sincronización,
 * obra, módulos y sesión en una barra lateral plegable; en móvil conserva una
 * cabecera compacta y abre los módulos en un drawer. El <Outlet /> ocupa el espacio restante.
 * El gate de sesión + onboarding vive en el beforeLoad de la ruta de layout (router.tsx), así
 * que las páginas hijas se renderizan ya autenticadas y onboarded.
 *
 * En móvil la fila mide 64px (h-16) y se le suma una franja de 40px (h-10) con la obra
 * seleccionada. De ahí `pt-26` en el contenido y `top-26` en el drawer. Desde `md` la cabecera
 * gira a columna, ocupa toda la altura y el contenido se desplaza 64px o 256px según el estado.
 *
 * El indicador de conexión (#426) es un bloque propio con una sola instancia: en escritorio el
 * orden del DOM lo deja justo bajo la marca y encima del selector de obra; en móvil `order-2` lo
 * sube a la fila superior, delante de cerrar sesión (que comparte `order-2` y va después en el DOM).
 */
export function AppLayout() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: me } = useMe();
  const logout = useLogout();
  const { collapsed, toggleCollapsed, open, setOpen } = useSidebar();
  // Obra activa (#252): la de la ruta si cuelga de /obras/$obraId y, si no, la recordada
  // mientras siga siendo válida. Así el menú no se vacía en las rutas globales (usuarios,
  // gestión de obras, sincronización, selector) y la navegación se mantiene estable.
  const obraActiva = useObraActiva();

  // Motor de sincronización (F4 #52): vigila la conectividad (navigator + heartbeat a /healthz) y
  // vacía la outbox al reconectar. Vive en el shell autenticado para que solo corra con sesión y
  // se detenga al desmontar. NO hay flush inicial propio (#237): al arrancar la conectividad está
  // sin confirmar, y la primera confirmación del heartbeat ya cuenta como reconexión — así el caso
  // «arrancar con outbox pendiente» se cubre sin empujar contra un backend que quizá no está.
  useEffect(
    () => startConnectivityMonitor({ onReconnect: () => void flushOutbox(queryClient) }),
    [queryClient],
  );

  // navItemsPara ya filtra por rol (admin/ADMIN_OBRA/CUALIFICADO y nº de obras): el backend
  // es el guardián real y los guards de ruta redirigen a quien fuerce la URL.
  const items = navItemsPara(me, obraActiva);

  async function onLogout() {
    await logout.mutateAsync();
    await navigate({ to: "/login" });
  }

  return (
    <div className="min-h-dvh bg-slate-100">
      {/* El mismo header cambia de eje en `md`, sin duplicar el ObraSwitcher: móvil lo reparte
          en dos filas y escritorio lo convierte en la barra lateral completa. */}
      <header
        className={cn(
          "fixed inset-x-0 top-0 z-50 flex h-26 flex-wrap items-center bg-navy-800 shadow-lg md:inset-y-0 md:right-auto md:h-dvh md:flex-col md:flex-nowrap md:items-stretch md:transition-[width] md:duration-200",
          collapsed ? "md:w-16" : "md:w-64",
        )}
      >
        <div
          className={cn(
            "order-1 flex h-16 items-center gap-2 pl-4 md:order-none md:w-full md:px-4",
            collapsed && "md:justify-center md:px-0",
          )}
        >
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="rounded-md p-1.5 text-slate-300 transition-colors hover:text-white md:hidden"
            aria-label="Abrir el menú"
            aria-expanded={open}
          >
            <Menu className="h-6 w-6" aria-hidden="true" />
          </button>
          <Link
            to="/"
            className={cn("flex items-center gap-2", collapsed && "md:justify-center")}
            aria-label={collapsed ? "LOTO System" : undefined}
          >
            <img src="/logo-loto.png" alt="" className="h-8 w-8" aria-hidden="true" />
            <span className={cn("font-bold text-white tracking-tight", collapsed && "md:hidden")}>
              LOTO System
            </span>
          </Link>
        </div>
        {/* #426: una sola instancia del indicador. En escritorio queda entre la marca y el
            selector de obra (alineado con la marca, o centrado y compacto en el rail plegado);
            en móvil `ml-auto` lo empuja a la derecha de la fila, delante de cerrar sesión. */}
        <div
          className={cn(
            "order-2 ml-auto flex h-16 items-center md:order-none md:ml-0 md:h-auto md:w-full md:px-4",
            collapsed && "md:justify-center md:px-0 md:pb-3",
          )}
        >
          <SyncIndicator compactoEnEscritorio={collapsed} />
        </div>
        <div
          className={cn(
            "order-3 flex h-10 w-full min-w-0 items-center px-4 md:order-none md:h-auto md:px-3 md:py-3",
            collapsed && "md:hidden",
          )}
        >
          <ObraSwitcher enBarraLateral />
        </div>

        <Sidebar
          items={items}
          collapsed={collapsed}
          toggleCollapsed={toggleCollapsed}
          open={open}
          setOpen={setOpen}
        />

        {/* Pie de la barra en escritorio: solo cerrar sesión. En móvil cierra la fila superior;
            el `pl-2` conserva la separación de 8px con el indicador que la precede. */}
        <div
          className={cn(
            "order-2 flex h-16 items-center pr-4 pl-2 md:order-none md:mt-auto md:h-auto md:w-full md:flex-col md:items-stretch md:p-3",
            collapsed && "md:px-2",
          )}
        >
          <Button
            variant="onDark"
            onClick={() => void onLogout()}
            disabled={logout.isPending}
            aria-label="Cerrar sesión"
            className={cn(collapsed ? "md:w-12 md:px-0" : "md:w-full md:justify-start", "shrink-0")}
          >
            {logout.isPending ? (
              <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
            ) : (
              <LogOut className="h-5 w-5" aria-hidden="true" />
            )}
            <span className={cn("hidden sm:inline", collapsed && "md:hidden")}>
              {me ? `${me.nombre} ${me.apellidos}`.trim() : "Salir"}
            </span>
          </Button>
        </div>
      </header>

      <div className="pt-26 md:pt-0">
        <main
          className={cn(
            "min-w-0 flex-1 px-4 py-6 transition-all duration-200 md:px-6",
            collapsed ? "md:ml-16" : "md:ml-64",
          )}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
